import { Fragment, useEffect, useState } from 'react'
import { Dialog, Transition } from '@headlessui/react'
import { CgSpinner } from 'react-icons/cg'
import Loading from '../Loading'
import { fetchGetBalance } from '../../utils/client/auth/user-api'
import { notify } from '../../utils/notifications'
import { useRouter } from 'next/router'

export default function StakingDepositDialog({ program, myDeposit, accessToken, requestDeposit, isLoading, onClose }) {
  const [myBalance, setMyBalance] = useState(undefined)
  const [inputDepositBalance, setInputDepositBalance] = useState(
    program.userDepositUnit === program.minUserDepositAmount ? program.userDepositUnit : '',
  )

  const router = useRouter()

  useEffect(() => {
    getMyTokenBalance()
  }, [])

  const handleRequestDeposit = () => {
    if (validInputValue(inputDepositBalance)) {
      requestDeposit(program.externalId, inputDepositBalance)
    }
  }

  async function getMyTokenBalance() {
    const response = await fetchGetBalance(accessToken, false)
    if (response.status === 200) {
      setMyBalance(response.data)
    } else if (response.status === 401 || response.status === 403) {
      notify({
        message: 'Please sign in again.',
        type: 'error',
      })
      router.push('/login?ref=/staking')
    }
  }

  function validInputValue(number) {
    if (number <= 0) {
      notify({
        message: `Invalid value.`,
        type: 'error',
      })
      return false
    } else if (number < program.minUserDepositAmount) {
      notify({
        message: `Deposit at least $${program.minUserDepositAmount}${program.depositToken}`,
        type: 'error',
      })
      return false
    } else if (number > program.maxUserDepositAmount) {
      notify({
        message: `Deposit maximum $${program.maxUserDepositAmount}${program.depositToken}`,
        type: 'error',
      })
      return false
    } else if (Number(number) % Number(program.userDepositUnit) != 0) {
      notify({
        message: `Deposit unit is ${program.userDepositUnit}${program.depositToken}`,
        type: 'error',
      })
      return false
    } else if (number > Number(program.maxDepositAmount) - Number(program.depositAmount)) {
      notify({
        message: `Cannot deposit more.(${program.depositAmount}/${program.maxDepositAmount})`,
        type: 'error',
      })
      return false
    }

    if (program.depositToken === 'WKM') {
      if (myBalance.wkmBalance < number) {
        notify({
          message: `Insufficient balance.`,
          type: 'error',
        })
        return false
      }
    } else if (program.depositToken === 'WKG') {
      if (myBalance.wkgBalance < number) {
        notify({
          message: `Insufficient balance.`,
          type: 'error',
        })
        return false
      }
    }

    if (myDeposit) {
      if (Number(myDeposit.depositAmount) + Number(number) > program.maxUserDepositAmount) {
        notify({
          message: `Deposit maximum ${program.maxUserDepositAmount}${program.depositToken}`,
          type: 'error',
        })
        return false
      }
    }

    return true
  }

  let depositTokenIcon = undefined
  if (program.depositToken === 'WKM') {
    depositTokenIcon = 'https://ad-banner.walkmining.com/web/ic_coin_wkm.png'
  } else if (program.depositToken === 'WKG') {
    depositTokenIcon = 'https://ad-banner.walkmining.com/web/ic_coin_wkg.png'
  } else {
    depositTokenIcon = undefined
  }

  return (
    <Transition.Root show={true} as={Fragment}>
      <Dialog as="div" className="relative z-10" onClose={() => {}}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
        </Transition.Child>

        <div className="fixed inset-0 z-50 w-screen overflow-y-auto bg-jacarta-200 bg-opacity-60">
          <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              enterTo="opacity-100 translate-y-0 sm:scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 translate-y-0 sm:scale-100"
              leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
            >
              <Dialog.Panel className="w-[350px] h-full max-w-md transform overflow-hidden rounded-2xl bg-white p-6 text-left align-middle shadow-xl transition-all">
                <Dialog.Title as="h3" className="text-lg font-bold leading-6 text-jacarta-900">
                  <span>Stake in pool</span>
                </Dialog.Title>
                <div className="relative mt-6">
                  <div className="absolute inset-0 flex items-center">
                    <div className="w-full border-t border-jacarta-100" />
                  </div>
                </div>
                <div className="flex items-center justify-between text-right mt-14">
                  <div className="block text-sm text-jacarta-700 ml-2 font-semibold">Stake</div>
                  <div className="flex items-center ml-2 text-sm text-jacarta-700 font-semibold">
                    {depositTokenIcon && <img className="h-7 pr-1" src={depositTokenIcon} />}
                    {program.depositToken}
                  </div>
                </div>
                <div className="h-full text-sm font-semibold">
                  <div className="mt-3 text-jacarta-700 focus:ring-accent border-jacarta-100 w-full rounded-2xl border py-[0.6875rem] px-4 pl-3">
                    {!myDeposit || program.maxUserDepositAmount > myDeposit.depositAmount ? (
                      <div>
                        <input
                          type="number"
                          disabled={!myBalance}
                          className="w-full text-right placeholder-jacarta-500 border-transparent focus:border-transparent focus:ring-0 pr-0"
                          placeholder="0.0"
                          value={inputDepositBalance}
                          min={program.minUserDepositAmount}
                          max={program.maxUserDepositAmount}
                          step={program.userDepositUnit}
                          onChange={(e) => {
                            if (!e.target.value || e.target.value > 0) {
                              setInputDepositBalance(e.target.value)
                            }
                          }}
                        />
                        {myBalance ? (
                          <span className="flex items-center justify-end text-[11px] text-jacarta-300 pr-3">
                            Balance: {program.depositToken === 'WKM' && `${myBalance.wkmBalance}`}
                            {program.depositToken === 'WKG' && `${myBalance.wkgBalance}`}
                          </span>
                        ) : (
                          <span className="flex items-center justify-end text-[11px] text-jacarta-300 pr-3">
                            Balance: <CgSpinner className={`animate-spin w-[13px] h-[13px] ml-1`} />
                          </span>
                        )}
                      </div>
                    ) : (
                      <div className="h-14">
                        <span className="flex pt-3 items-center justify-center text-jacarta-700 text-center">
                          Maximum amount staked completed!
                        </span>
                        <span className="flex items-center justify-center text-jacarta-500 text-sm text-center">
                          Your staked {myDeposit.depositAmount} {myDeposit.depositToken}
                        </span>
                      </div>
                    )}
                  </div>
                </div>
                <div className="mt-6 mb-6">
                  <div className="flex items-center justify-between text-right">
                    <div className="block text-[12px] text-jacarta-400 ml-2">Total staked</div>
                    <div className="ml-2 text-sm text-jacarta-700 font-semibold">
                      {program.depositAmount}/{program.maxDepositAmount}
                    </div>
                  </div>
                  <div className="flex items-center justify-between text-right mt-1">
                    <div className="block text-[12px] text-jacarta-400 ml-2">Min deposit amount</div>
                    <div className="ml-2 text-sm text-jacarta-700 font-semibold">{program.minUserDepositAmount}</div>
                  </div>
                  <div className="flex items-center justify-between text-right mt-1">
                    <div className="block text-[12px] text-jacarta-400 ml-2">Max deposit amount</div>
                    <div className="ml-2 text-sm text-jacarta-700 font-semibold">{program.maxUserDepositAmount}</div>
                  </div>
                  <div className="flex items-center justify-between text-right mt-1">
                    <div className="block text-[12px] text-jacarta-400 ml-2">Deposit unit</div>
                    <div className="ml-2 text-sm text-jacarta-700 font-semibold">{program.userDepositUnit}</div>
                  </div>
                  {myDeposit && (
                    <div className="flex items-center justify-between text-right mt-1">
                      <div className="block text-[12px] text-jacarta-400 ml-2 font-semibold">
                        Available deposit amount
                      </div>
                      <div className="ml-2 text-sm text-jacarta-700 font-semibold">
                        {myDeposit.depositAmount}/{program.maxUserDepositAmount}
                      </div>
                    </div>
                  )}
                </div>
                <div className="mb-4">
                  <button
                    type="button"
                    disabled={isLoading || inputDepositBalance === 0}
                    className="flex justify-center items-center w-full h-12 hover:bg-jacarta-600 text-[16px] rounded-full bg-[#131740] text-white"
                    onClick={() => {
                      if (!myDeposit || program.maxUserDepositAmount > myDeposit.depositAmount) {
                        handleRequestDeposit()
                      } else {
                        onClose()
                      }
                    }}
                  >
                    {isLoading ? (
                      <div>
                        <Loading />
                      </div>
                    ) : (
                      'Confirm'
                    )}
                  </button>

                  <button type="button" className="w-full mt-3 text-sm text-jacarta-600" onClick={onClose}>
                    Close
                  </button>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition.Root>
  )
}
